import { cn } from '@/lib/utils';
import { Check, Package, Truck, CheckCircle, XCircle, Circle } from 'lucide-react';

/**
 * Logistics-only timeline – payment status is shown separately via PaymentStatusBadge.
 * This keeps the progress bar meaningful regardless of whether Stripe is active or paused.
 */
const STATUS_STEPS = [
  { key: 'created', label: 'Mottagen', icon: Circle },
  { key: 'processing', label: 'Förbereds', icon: Package },
  { key: 'ready', label: 'Redo att hämta', icon: Truck },
  { key: 'completed', label: 'Hämtad', icon: CheckCircle },
] as const;

const STATUS_ORDER: Record<string, number> = {
  created: 0,
  paid: 0,        // 'paid' maps to same step as 'created' (Mottagen) – payment is shown separately
  processing: 1,
  ready: 2,
  completed: 3,
  cancelled: -1,
};

interface OrderStatusTimelineProps {
  status: string;
  className?: string;
}

export function OrderStatusTimeline({ status, className }: OrderStatusTimelineProps) {
  const currentIndex = STATUS_ORDER[status] ?? 0;
  const isCancelled = status === 'cancelled';

  if (isCancelled) {
    return (
      <div className={cn('flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20', className)}>
        <XCircle className="h-6 w-6 text-destructive shrink-0" />
        <div>
          <p className="font-medium text-destructive">Ordern är avbruten</p>
          <p className="text-sm text-muted-foreground">Kontakta gården om du har frågor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      {/* Mobile: Vertical timeline */}
      <div className="flex flex-col gap-0 sm:hidden">
        {STATUS_STEPS.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const StepIcon = step.icon;

          return (
            <div key={step.key} className="flex items-start gap-3">
              {/* Icon and line */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
                    isCompleted
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-muted border-muted-foreground/30 text-muted-foreground'
                  )}
                >
                  {isCompleted && index < currentIndex ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <StepIcon className="h-4 w-4" />
                  )}
                </div>
                {index < STATUS_STEPS.length - 1 && (
                  <div
                    className={cn(
                      'w-0.5 h-6',
                      index < currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                    )}
                  />
                )}
              </div>
              {/* Label */}
              <div className="pb-6">
                <p
                  className={cn(
                    'text-sm font-medium leading-8',
                    isCurrent ? 'text-foreground' : isCompleted ? 'text-muted-foreground' : 'text-muted-foreground/60'
                  )}
                >
                  {step.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: Horizontal timeline */}
      <div className="hidden sm:flex items-center justify-between gap-2">
        {STATUS_STEPS.map((step, index) => {
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const StepIcon = step.icon;

          return (
            <div key={step.key} className="flex flex-1 items-center">
              <div className="flex flex-col items-center gap-2">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    isCompleted
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'bg-muted border-muted-foreground/30 text-muted-foreground'
                  )}
                >
                  {isCompleted && index < currentIndex ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <StepIcon className="h-5 w-5" />
                  )}
                </div>
                <p
                  className={cn(
                    'text-xs font-medium text-center',
                    isCurrent ? 'text-foreground' : isCompleted ? 'text-muted-foreground' : 'text-muted-foreground/60'
                  )}
                >
                  {step.label}
                </p>
              </div>
              {index < STATUS_STEPS.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2',
                    index < currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
